import { findUser } from '@/api/users-api'
import { userKeys } from '@/hooks/users/query-keys'
import { useAuthStore } from '@/store/auth'
import { useUserStore } from '@/store/user'
import { supabase } from '@/lib/supabase'
import { getHookClaims } from '@/lib/auth-claims'
import { redirect } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { LOGIN_PATH, authRefusalReasonOf } from '@/constants/login'
import type { QueryClient } from '@tanstack/react-query'

export async function authenticateBeforeLoad(location: {
  href: string
}): Promise<{ userId: string }> {
  try {
    // Read the session LOCALLY (supabase-js owns it in localStorage). No
    // per-navigation network round-trip — the SDK keeps it fresh and the
    // auth-bridge listener mirrors it into the Zustand store + Realtime socket.
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session) throw new Error('No active session')

    const { userId, organizationId, role } = getHookClaims(session)

    if (!userId || !organizationId)
      throw new Error('Invalid session: missing user or org id')

    useAuthStore.getState().setAuth(userId, organizationId, role)

    return { userId }
  } catch (err) {
    console.error('Authentication check failed:', err)
    useAuthStore.getState().reset()
    throw redirect({
      to: '/login',
      search: { redirect: location.href },
    })
  }
}

export async function loadAuthenticatedUser(
  queryClient: QueryClient,
  userId: string | null
) {
  if (!userId) return

  try {
    const user = await queryClient.ensureQueryData({
      queryKey: userKeys.detail(userId),
      queryFn: () => findUser(userId),
    })
    useUserStore.getState().setUser(user)
  } catch (err) {
    // A locally-valid session the API rejects — e.g. an abandoned invite-link
    // half-session: the JWT carries hook claims regardless of status, but the
    // guard 401s every non-ACTIVE user. Drop the session instead of parking
    // the user in a 401-looping app shell. (The connector has already retried
    // once after a refresh before this surfaces.)
    if (isAxiosError(err) && err.response?.status === 401) {
      await supabase.auth.signOut().catch(() => {})
      useAuthStore.getState().reset()
      throw redirect({
        to: LOGIN_PATH,
        search: { reason: authRefusalReasonOf(err) },
      })
    }
    throw err
  }
}
